import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Tag, Popconfirm, message, Modal, Select, Typography } from 'antd';
import { PlusOutlined, DeleteOutlined, TeamOutlined } from '@ant-design/icons';
import { CompanyInstanceService } from '../../services/api/companyInstanceService';
import { CompanyInstance } from '../../types/companyInstance';
import { Company } from '../../types/company';
import { UserService } from '../../services/api/userService';
import { apiClient } from '../../services/apiClient';
import { getApiErrorMessage } from '../../utils/apiErrors';

const { Text } = Typography;
const { Option } = Select;

interface UserCompanyTabProps {
  userId: string;
}

interface CompanyInstanceWithDetails extends CompanyInstance {
  company?: Company | null;
}

export const UserCompanyTab: React.FC<UserCompanyTabProps> = ({ userId }) => {
  const [companyInstances, setCompanyInstances] = useState<CompanyInstanceWithDetails[]>([]);
  const [allCompanies, setAllCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [isAssignModalVisible, setIsAssignModalVisible] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');

  useEffect(() => {
    if (userId) {
      loadUserCompanies();
      loadAllCompanies();
    }
  }, [userId]);

  const loadAllCompanies = async () => {
    try {
      const response = await apiClient.get<Company[]>('/crud/companies', { params: { limit: 1000 } });
      setAllCompanies(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setAllCompanies([]);
      message.error(getApiErrorMessage(error, 'Failed to load companies'));
    }
  };

  const loadUserCompanies = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get<Array<CompanyInstance & { company_name?: string }>>('/company-instances', {
        params: { user_id: userId, active_only: false, limit: 1000 },
      });
      const instances = Array.isArray(response.data) ? response.data : [];
      if (instances.length >= 1000) {
        message.warning('Company memberships are capped at 1,000 for this user.');
      }
      setCompanyInstances(
        instances
          .filter((instance) => Boolean(instance.company_id))
          .map((instance) => ({
            ...instance,
            company: {
              id: instance.company_id,
              name: instance.company_name || instance.company_id,
            } as Company,
          })),
      );
    } catch (error) {
      message.error(getApiErrorMessage(error, 'Failed to load companies'));
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (instanceId: string) => {
    try {
      await CompanyInstanceService.delete(instanceId);
      message.success('Company removed successfully');
      await loadUserCompanies();
    } catch (error: unknown) {
      message.error(getApiErrorMessage(error, 'Failed to remove company'));
    }
  };

  const handleAssign = async () => {
    if (!selectedCompanyId) {
      message.warning('Please select a company');
      return;
    }

    // Check if user is already in this company
    const existing = companyInstances.find(inst => inst.company_id === selectedCompanyId);
    if (existing) {
      message.warning('User is already in this company');
      return;
    }

    try {
      setLoading(true);
      // Use UserService to add user to company
      await UserService.addToCompany(userId, selectedCompanyId);
      message.success('Company assigned successfully');
      setIsAssignModalVisible(false);
      setSelectedCompanyId('');
      await loadUserCompanies();
    } catch (error: unknown) {
      message.error(getApiErrorMessage(error, 'Failed to assign company'));
    } finally {
      setLoading(false);
    }
  };

  const availableCompanies = allCompanies.filter(
    (company) => !companyInstances.some((inst) => inst.company_id === company.id)
  );

  const columns = [
    {
      title: 'Company',
      key: 'company',
      render: (record: CompanyInstanceWithDetails) => (
        <Space>
          <TeamOutlined />
          <Text strong>{record.company?.name || record.instance_name || 'Unknown Company'}</Text>
        </Space>
      ),
    },
    {
      title: 'Instance Name',
      dataIndex: 'instance_name',
      key: 'instance_name',
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => role ? <Tag>{role}</Tag> : 'N/A',
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Joined',
      dataIndex: 'joined_at',
      key: 'joined_at',
      render: (date: string) => date ? new Date(date).toLocaleDateString() : 'N/A',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: CompanyInstanceWithDetails) => (
        <Popconfirm
          title="Are you sure you want to remove this company?"
          onConfirm={() => handleRemove(record.id)}
        >
          <Button type="link" danger size="small" icon={<DeleteOutlined />}>
            Remove
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setIsAssignModalVisible(true)}
          >
            Add Company
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={companyInstances}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} companies`,
          }}
        />
      </Space>

      <Modal
        title="Add Company to User"
        open={isAssignModalVisible}
        onOk={handleAssign}
        onCancel={() => {
          setIsAssignModalVisible(false);
          setSelectedCompanyId('');
        }}
        confirmLoading={loading}
        okText="Add"
        cancelText="Cancel"
      >
        <Select
          style={{ width: '100%' }}
          placeholder="Select a company"
          value={selectedCompanyId || undefined}
          onChange={setSelectedCompanyId}
          showSearch
          filterOption={(input, option) =>
            (option?.children as unknown as string)
              ?.toLowerCase()
              .includes(input.toLowerCase())
          }
        >
          {availableCompanies.map((company) => (
            <Option key={company.id} value={company.id}>
              {company.name}
            </Option>
          ))}
        </Select>
      </Modal>
    </div>
  );
};

